import React, { useEffect, useMemo, useState } from 'react';
import IconAdd from '@mui/icons-material/Add';
import IconEdit from '@mui/icons-material/Edit';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import styles from './aims.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import DateChip from '../moduleCard/DateChip';
import AimsPopup from '../../../../popup/AimsPopup/AimsPopup';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import AimsTable from './AimsTable';
import AimsChart from './AimsChart';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildAimsJob } from '../../modules/reportSections';
import { AIMS_ATTRIBUTION, AGE_MAX_MONTHS, BAND_NOTE, TOTAL_MAX, findOwnRecord, formatAge, formatDate, formatPercentile, isOutOfRange } from '../../modules/aims/aims';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.AIMS];
const TONE_CLASS = { alert: styles.bandAlert, info: styles.bandInfo, ok: styles.bandOk };

/**
 * Módulo "Escala motora de Alberta (AIMS)": escore de cada postura aplicado
 * com o material oficial, total, percentil e faixa, com o histórico do
 * paciente. Uma avaliação por atendimento. Os itens e as curvas da escala não
 * são reproduzidos (licença dos autores).
 *
 * `data`: avaliações do paciente (todas, de qualquer atendimento).
 * `birthInfo`: idade gestacional ao nascer (patient_birth_info) ou null.
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const Aims = ({
  data,
  birthInfo,
  birthday,
  patientId,
  appointmentId,
  appointmentDate,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const [history, setHistory] = useState(Array.isArray(data) ? data : []);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    setHistory(Array.isArray(data) ? data : []);
  }, [data]);

  const ownEntry = findOwnRecord(history, appointmentId);
  const job = useMemo(
    () => buildAimsJob(history, { birthday }, birthInfo, ownEntry),
    [history, birthday, birthInfo, ownEntry]
  );

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildAimsJob(history, { birthday }, birthInfo, ownEntry),
  });

  function popupHideHandler(needsReload) {
    setPopup(null);
    if (needsReload && onUpdated) onUpdated();
  }

  const featured = job && job.featured;

  const actions = (
    <>
      {job && <ModulePdfButton report={report} />}
      {enabled && (ownEntry ? (
        <CustomButton
          variant="outlined"
          style="primary"
          label="Editar avaliação"
          icon={<IconEdit />}
          onClick={() => setPopup({ entry: ownEntry })}
        />
      ) : (
        <CustomButton
          variant="contained"
          style="primary"
          label="Nova avaliação"
          icon={<IconAdd />}
          onClick={() => setPopup({ entry: null })}
        />
      ))}
    </>
  );

  return (
    <>
      <ModuleCard
        accent="teal"
        title="Escala motora de Alberta (AIMS)"
        icon={<AccessibilityNewOutlinedIcon />}
        description="Escore por postura, total e percentil, de 0 a 18 meses"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {!job ? (
            <ModuleEmptyState icon={<AccessibilityNewOutlinedIcon />} text="Nenhuma avaliação pela escala de Alberta registrada." />
          ) : (
            <>
              <div className={styles.context}>
                <DateChip label={formatDate(featured.record.assessedAt)} />
                {featured.age && <span className={parts.chip}>Idade: {formatAge(featured.age)}</span>}
                <span className={parts.chip}>Total: {featured.total}/{TOTAL_MAX}</span>
                <span className={parts.chip}>Percentil: {formatPercentile(featured.record.percentile)}</span>
                {featured.band && (
                  <span className={`${styles.band} ${TONE_CLASS[featured.band.tone]}`}>{featured.band.label}</span>
                )}
              </div>

              {isOutOfRange(featured.age) && (
                <div className={styles.hint}>
                  A escala foi feita para bebês de 0 a {AGE_MAX_MONTHS} meses. Acima disso, interprete o escore com cautela.
                </div>
              )}

              <AimsChart rows={job.rows} highlightId={featured.record.id} />

              <AimsTable rows={job.rows} highlightId={featured.record.id} />

              {featured.record.notes && <div className={styles.notes}>{featured.record.notes}</div>}

              <span className={styles.footnote}>
                {BAND_NOTE} {AIMS_ATTRIBUTION}
              </span>
            </>
          )}
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {popup && (
        <AimsPopup
          patientId={patientId}
          appointmentId={appointmentId}
          appointmentDate={appointmentDate}
          birthday={birthday}
          birthInfo={birthInfo}
          entry={popup.entry}
          OnHide={popupHideHandler}
        />
      )}
    </>
  );
};

export default Aims;
